import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Shield, Users, Store, HeartHandshake, Sparkles, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import api, { authStore } from "../lib/api";

/**
 * /admin — Superuser panel. Reachable via OTP login with any mobile in
 * ADMIN_MOBILES (currently 8802472191). Backend auto-promotes is_admin and
 * returns route.kind='admin' from /api/auth/verify-otp so the frontend routes
 * straight here without touching the standard home hub.
 */
const Stat = ({ testid, icon: Icon, label, value }) => (
  <div data-testid={testid} className="editorial-card p-5 flex items-start gap-4">
    <div className="w-11 h-11 flex items-center justify-center shrink-0" style={{ background: "rgba(122,14,24,0.06)", border: "1px solid var(--crimson)" }}>
      <Icon size={18} style={{ color: "var(--crimson)" }}/>
    </div>
    <div>
      <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>{label}</p>
      <p className="mt-1 font-serif text-3xl" style={{ color: "var(--ink)" }}>{value}</p>
    </div>
  </div>
);

const Section = ({ title, action, children }) => (
  <section className="mt-10">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-serif text-2xl" style={{ color: "var(--crimson)" }}>{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export default function AdminPanel() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vouches, setVouches] = useState([]);
  const [leads, setLeads] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const [meR, statsR, vendorsR, vouchesR, leadsR] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/stats"),
        api.get("/admin/vendors?status=pending"),
        api.get("/admin/vouches"),
        api.get("/admin/leads"),
      ]);
      setMe(meR.data.user);
      setStats(statsR.data);
      setVendors(vendorsR.data.vendors || []);
      setVouches(vouchesR.data.vouches || []);
      setLeads(leadsR.data.leads || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load admin panel");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!authStore.token()) { nav("/"); return; }
    refresh();
  }, [nav]);

  const approveVendor = async (id) => {
    try {
      await api.post(`/admin/vendors/${id}/approve`, { note: "Approved from admin panel" });
      toast.success("Vendor approved");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const rejectVendor = async (id) => {
    try {
      await api.post(`/admin/vendors/${id}/reject`, { note: "Rejected from admin panel" });
      toast.success("Vendor rejected");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };
  const verifyVouch = async (id) => {
    try {
      await api.post(`/admin/vouches/${id}/verify`);
      toast.success("Vouch verified");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
  };
  const logout = () => { authStore.clear(); nav("/"); };

  return (
    <div data-testid="admin-panel" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <div className="flex items-center gap-3">
          <BrandLogo size="sm"/>
          <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1"
            style={{ background: "rgba(122,14,24,0.10)", color: "var(--crimson)", border: "1px solid var(--crimson)" }}>
            <Shield size={11}/> Admin
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={refresh} data-testid="admin-refresh"
            className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--crimson)" }}>
            <RefreshCw size={14}/> Refresh
          </button>
          <button onClick={logout} data-testid="admin-logout"
            className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
            <LogOut size={14}/> Logout
          </button>
        </div>
      </header>

      <section className="px-6 md:px-10 pt-10 pb-4">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow flex items-center gap-2"><Sparkles size={12}/> Superuser</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-2" style={{ color: "var(--crimson)" }}>
            Vedika Command Center
          </h1>
          {me && <p className="mt-2 text-sm" style={{ color: "var(--ash)" }}>Logged in as <strong>{me.mobile}</strong> · Session persists 30 days.</p>}
        </div>
      </section>

      <section className="px-6 md:px-10 pb-16 flex-1">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats-grid">
            <Stat testid="stat-users"    icon={Users}          label="Total Users"        value={stats?.users ?? "—"}/>
            <Stat testid="stat-profiles" icon={HeartHandshake} label="Completed Profiles" value={stats?.profiles?.total ?? "—"}/>
            <Stat testid="stat-verified" icon={CheckCircle2}   label="Aadhaar Verified"   value={stats?.profiles?.aadhaar_verified ?? "—"}/>
            <Stat testid="stat-vendors"  icon={Store}          label="Vendors"            value={stats?.vendors?.total ?? "—"}/>
          </div>

          <Section title="Pending Vendor Approvals" action={<span className="eyebrow" data-testid="pending-vendor-count">{vendors.length} pending</span>}>
            {vendors.length === 0 ? (
              <p data-testid="no-pending-vendors" className="text-sm italic" style={{ color: "var(--ash)" }}>All caught up — no vendors awaiting approval.</p>
            ) : (
              <div className="space-y-3" data-testid="pending-vendor-list">
                {vendors.map((v) => (
                  <div key={v.id} data-testid={`vendor-${v.id}`} className="editorial-card p-4 flex items-center justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg" style={{ color: "var(--ink)" }}>{v.business_name}</p>
                      <p className="text-xs" style={{ color: "var(--ash)" }}>
                        {v.category || "—"} · {v.city || "—"} · Mobile {v.mobile}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`vendor-${v.id}-approve`} onClick={() => approveVendor(v.id)}
                        className="btn-primary text-[11px] py-2 px-3">
                        <CheckCircle2 size={12}/> Approve
                      </button>
                      <button data-testid={`vendor-${v.id}-reject`} onClick={() => rejectVendor(v.id)}
                        className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1" style={{ color: "var(--crimson)" }}>
                        <XCircle size={12}/> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Digital Vouches" action={<span className="eyebrow" data-testid="vouch-count">{vouches.length} entries</span>}>
            {vouches.length === 0 ? (
              <p className="text-sm italic" style={{ color: "var(--ash)" }}>No vouches submitted yet.</p>
            ) : (
              <div className="space-y-3" data-testid="vouch-list">
                {vouches.slice(0, 12).map((v) => (
                  <div key={v.id} data-testid={`vouch-${v.id}`} className="editorial-card p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-serif text-lg" style={{ color: "var(--ink)" }}>{v.voucher_name} <span className="text-xs" style={{ color: "var(--ash)" }}>({v.voucher_role})</span></p>
                      <p className="text-xs mt-1" style={{ color: "var(--ash)" }}>Vouching for: {v.vouched_profile_name}</p>
                      <p className="text-xs mt-1" style={{ color: "var(--ash)" }}>Status: <strong style={{ color: v.status === "verified" ? "var(--leaf)" : "var(--crimson)" }}>{v.status}</strong></p>
                    </div>
                    {v.status !== "verified" && (
                      <button data-testid={`vouch-${v.id}-verify`} onClick={() => verifyVouch(v.id)}
                        className="btn-primary text-[11px] py-2 px-3">
                        <CheckCircle2 size={12}/> Verify
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Marriage Leads" action={<span className="eyebrow" data-testid="lead-count">{leads.length} leads</span>}>
            {leads.length === 0 ? (
              <p className="text-sm italic" style={{ color: "var(--ash)" }}>No marriage service leads.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lead-list">
                {leads.slice(0, 10).map((l) => (
                  <div key={l.id} className="editorial-card p-4">
                    <p className="font-serif text-lg" style={{ color: "var(--ink)" }}>{l.service_type || "Requirement"}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--ash)" }}>{l.city || "—"} · {l.event_date || "—"}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--ash)" }}>Contact: {l.masked_mobile || l.mobile}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--ash)" }}>Status: {l.status || "open"}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {busy && <p className="text-center mt-10 text-xs tracking-widest uppercase" style={{ color: "var(--ash)" }}>Refreshing…</p>}
        </div>
      </section>
    </div>
  );
}
